import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * GalleryModal - gallery with full-size image viewer, pinch-to-zoom (mobile)
 * and scroll-wheel zoom (desktop).
 *
 * Uses shared UploadLightbox component for consistent experience with admin uploads.
 * Includes Photo and Upload buttons for adding new images directly from the gallery.
 */
import React, { useEffect, useState, useCallback } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, } from '../../components/ui/dialog';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Camera, Download, Loader2, Trash2, Upload } from 'lucide-react';
import { UploadLightbox } from './uploads/UploadLightbox';
import { formatFileSize, formatUploadDate, isImageFile, processImageUrl, getFullSizeUrl, } from '../../utils/uploads';
import { usePhotoUpload } from '../../features/home/hooks/usePhotoUpload';
import { DialogProvider } from '../../features/home/context/DialogContext';
/**
 * Inner component that uses the upload hook (requires DialogProvider)
 */
const GalleryModalInner = ({ open, onClose, objectType, objectId, objectTitle, structureCoords, onDelete, onConfirmRemoveTag, }) => {
    const [uploads, setUploads] = useState([]);
    const [loading, setLoading] = useState(false);
    const [lightboxIndex, setLightboxIndex] = useState(-1);
    // Photo upload hook for camera and file upload functionality
    const { isUploading, triggerCamera, triggerUpload, renderFileInputs, } = usePhotoUpload({
        objectType,
        objectId,
        structureCoords,
        onUploadComplete: () => {
            // Refresh the uploads list after successful upload
            fetchUploads();
        },
    });
    const fetchUploads = async () => {
        setLoading(true);
        try {
            const response = await fetch(`/upload/assets/${objectType}/${objectId}`);
            if (response.ok) {
                const data = await response.json();
                setUploads(Array.isArray(data) ? data : []);
            }
            else {
                throw new Error('Failed to fetch uploads');
            }
        }
        catch (error) {
            console.error('Failed to load uploads:', error);
            alert(`Error: ${error instanceof Error ? error.message : 'Failed to load uploads'}`);
        }
        finally {
            setLoading(false);
        }
    };
    useEffect(() => {
        if (open && objectId) {
            fetchUploads();
        }
    }, [open, objectId]);
    const handleDelete = async (upload) => {
        let confirmed = false;
        if (onDelete) {
            confirmed = await onDelete(upload);
        }
        else {
            confirmed = window.confirm(`Are you sure you want to delete ${upload.filename}?`);
        }
        if (!confirmed)
            return;
        try {
            const response = await fetch(`/upload/assets/${upload.id}`, {
                method: 'DELETE',
            });
            const result = await response.json();
            if (result.status === 'OK') {
                setUploads((prev) => prev.filter((u) => u.id !== upload.id));
            }
            else {
                throw new Error(result.msg || 'Delete failed');
            }
        }
        catch (error) {
            console.error('Failed to delete upload:', error);
            alert(`Error: ${error instanceof Error ? error.message : 'Failed to delete upload'}`);
        }
    };
    const isImage = (upload) => isImageFile(upload);
    const getThumbnailUrl = (url) => processImageUrl(url, 'small');
    const getOriginalUrl = (url) => getFullSizeUrl(url);
    // Filter to only image uploads for lightbox
    const imageUploads = uploads.filter(isImage);
    // Track whether we pushed a history state for the lightbox
    const lightboxHistoryPushedRef = React.useRef(false);
    // Handle thumbnail click - open lightbox at correct index
    const handleImageClick = (upload) => {
        const imageIndex = imageUploads.findIndex((u) => u.id === upload.id);
        if (imageIndex >= 0) {
            setLightboxIndex(imageIndex);
            // Push history state for back button navigation
            window.history.pushState({ lightboxOpen: true }, '');
            lightboxHistoryPushedRef.current = true;
        }
    };
    // Close lightbox handler - handles both X button and back button
    const closeLightbox = useCallback((viaBackButton = false) => {
        setLightboxIndex(-1);
        // If we pushed a history state and this isn't being closed via the back button,
        // we need to pop the history state to clean up
        if (lightboxHistoryPushedRef.current && !viaBackButton) {
            lightboxHistoryPushedRef.current = false;
            // Check if current state has our lightbox flag before going back
            if (window.history.state?.lightboxOpen) {
                window.history.back();
            }
        }
        else {
            lightboxHistoryPushedRef.current = false;
        }
    }, []);
    // Listen for back button to close lightbox
    useEffect(() => {
        if (lightboxIndex < 0)
            return;
        const handlePopState = (event) => {
            if (!event.state?.lightboxOpen) {
                // Close via back button - don't trigger another history.back()
                closeLightbox(true);
            }
        };
        window.addEventListener('popstate', handlePopState);
        return () => window.removeEventListener('popstate', handlePopState);
    }, [lightboxIndex, closeLightbox]);
    const formatDate = (dateString) => formatUploadDate(dateString);
    // Tag management functions
    const addTag = useCallback(async (uploadId, tag) => {
        try {
            const response = await fetch(`/upload/assets/${uploadId}/tag`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ tag }),
            });
            const result = await response.json();
            if (result.status === 'OK' && result.data) {
                setUploads((prev) => prev.map((u) => u.id === uploadId
                    ? { ...u, UploadTags: [...(u.UploadTags || []), result.data] }
                    : u));
                return true;
            }
            return false;
        }
        catch (error) {
            console.error('Failed to add tag:', error);
            return false;
        }
    }, []);
    const removeTag = useCallback(async (uploadId, tag) => {
        try {
            const response = await fetch(`/upload/assets/${uploadId}/tag/${encodeURIComponent(tag)}`, {
                method: 'DELETE',
            });
            const result = await response.json();
            if (result.status === 'OK') {
                setUploads((prev) => prev.map((u) => u.id === uploadId
                    ? { ...u, UploadTags: (u.UploadTags || []).filter((t) => t.tag !== tag) }
                    : u));
                return true;
            }
            return false;
        }
        catch (error) {
            console.error('Failed to remove tag:', error);
            return false;
        }
    }, []);
    // Prepare uploads for lightbox with full URLs
    const lightboxUploads = imageUploads.map((upload) => ({
        ...upload,
        url: getOriginalUrl(upload.url),
    }));
    // When lightbox is open, show ONLY the lightbox (no Radix Dialog)
    if (lightboxIndex >= 0) {
        return (_jsx(UploadLightbox, { uploads: lightboxUploads, currentIndex: lightboxIndex, onClose: closeLightbox, onIndexChange: setLightboxIndex, onAddTag: addTag, onRemoveTag: removeTag, onConfirmRemoveTag: onConfirmRemoveTag, showTags: true, showUploader: true, showMetadata: true }));
    }
    return (_jsx(Dialog, { open: open, onOpenChange: onClose, children: _jsxs(DialogContent, { fullScreenMobile: true, className: "sm:max-w-4xl overflow-hidden flex flex-col [&>button]:bg-muted [&>button]:hover:bg-muted/80 [&>button]:text-foreground [&>button]:border [&>button]:border-border", children: [_jsx(DialogHeader, { className: "flex-shrink-0", children: _jsxs("div", { className: "flex items-center justify-between gap-4", children: [_jsxs("div", { children: [_jsxs(DialogTitle, { children: ["Photo Gallery ", objectTitle && `- ${objectTitle}`] }), _jsxs(DialogDescription, { className: "text-muted-foreground", children: [uploads.length, " upload", uploads.length !== 1 ? 's' : ''] })] }), _jsxs("div", { className: "flex gap-2", children: [_jsxs(Button, { size: "sm", variant: "outline", onClick: triggerCamera, disabled: isUploading, title: "Take photo", children: [_jsx(Camera, { className: "h-4 w-4 mr-1.5" }), "Photo"] }), _jsxs(Button, { size: "sm", variant: "outline", onClick: triggerUpload, disabled: isUploading, title: "Upload files", children: [_jsx(Upload, { className: "h-4 w-4 mr-1.5" }), "Upload"] })] })] }) }), renderFileInputs(), _jsx("div", { className: "flex-1 overflow-y-auto", children: loading ? (_jsxs("div", { className: "flex items-center justify-center py-12", children: [_jsx(Loader2, { className: "h-8 w-8 animate-spin text-muted-foreground" }), _jsx("span", { className: "ml-2 text-muted-foreground", children: "Loading uploads..." })] })) : uploads.length === 0 ? (_jsxs("div", { className: "flex flex-col items-center justify-center py-12 text-muted-foreground", children: [_jsx("p", { className: "text-lg font-medium", children: "No uploads yet" }), _jsx("p", { className: "text-sm mt-1", children: "Use the Photo or Upload buttons above to add images" })] })) : (_jsx("div", { className: "grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 p-4", children: uploads.map((upload) => (_jsxs("div", { className: `relative group rounded-lg overflow-hidden border border-border hover:border-muted-foreground transition-colors ${isImage(upload) ? 'cursor-pointer' : ''}`, onClick: () => isImage(upload) && handleImageClick(upload), children: [_jsxs("div", { className: "aspect-square bg-muted", children: [isImage(upload) && upload.url ? (_jsx("img", { src: getThumbnailUrl(upload.url), alt: upload.filename, className: "w-full h-full object-cover", loading: "lazy", onError: (e) => {
                                                e.currentTarget.style.display = 'none';
                                                const placeholder = e.currentTarget.parentElement?.querySelector('.placeholder-fallback');
                                                if (placeholder)
                                                    placeholder.style.display = 'flex';
                                            } })) : null, _jsx("div", { className: "placeholder-fallback w-full h-full flex items-center justify-center", style: { display: isImage(upload) && upload.url ? 'none' : 'flex' }, children: _jsx("span", { className: "text-4xl text-muted-foreground", children: "\uD83D\uDCC4" }) })] }), _jsxs("div", { className: "absolute top-2 right-2 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity", children: [_jsx(Button, { size: "icon", variant: "secondary", className: "h-8 w-8", onClick: (e) => {
                                                e.stopPropagation();
                                                window.open(upload.url, '_blank');
                                            }, title: "Download", children: _jsx(Download, { className: "h-4 w-4" }) }), _jsx(Button, { size: "icon", variant: "destructive", className: "h-8 w-8", onClick: (e) => {
                                                e.stopPropagation();
                                                handleDelete(upload);
                                            }, title: "Delete", children: _jsx(Trash2, { className: "h-4 w-4" }) })] }), _jsxs("div", { className: "p-2 bg-card", children: [_jsx("p", { className: "text-xs font-medium truncate", title: upload.filename, children: upload.filename }), _jsx("p", { className: "text-xs text-muted-foreground", children: formatFileSize(upload.size) }), upload.lastModified && (_jsx("p", { className: "text-xs text-muted-foreground/70 mt-0.5", children: formatDate(new Date(upload.lastModified).toISOString()) })), upload.uploader && (_jsxs("p", { className: "text-xs text-muted-foreground/70 mt-0.5 truncate", title: upload.uploader.name, children: ["by ", upload.uploader.name] })), upload.UploadTags && upload.UploadTags.length > 0 && (_jsx("div", { className: "flex flex-wrap gap-1 mt-1.5", children: upload.UploadTags.map((t, idx) => (_jsx(Badge, { variant: "secondary", className: "text-[10px] px-1.5 py-0", children: t.tag }, `${t.tag}-${idx}`))) }))] })] }, upload.id))) })) })] }) }));
};
/**
 * GalleryModal wrapper - provides DialogProvider for the upload hook
 */
export const GalleryModal = (props) => {
    return (_jsx(DialogProvider, { children: _jsx(GalleryModalInner, { ...props }) }));
};
export default GalleryModal;
